import { useCallback, useState } from "react";

let current = 0;
const generateId = (prefix) => `${prefix || "id"}-${current++}`;

const addEditor = (editorType, record, recordIdObj, setEditors, options = {}) => {
  const editorRecord = {
    id: generateId("editor-id"),
    record,
    recordIdObj,
    type: editorType,
    className: options.className
  };
  setEditors((prevEditors) => [editorRecord, ...prevEditors]);
  return editorRecord;
};

const removeEditorById = (openEditor, setEditors) => {
  setEditors((prevEditors) => prevEditors.filter((editor) => editor.id !== openEditor.id));
};

const removeEditorsByType = (setEditors, editorType) => {
  if (!editorType) {
    //Closing all editors while logout/board change
    setEditors((prevState) => {
      prevState.forEach((editor) => {
        if (editor.editorRef && editor.editorRef.isWidget) {
          editor.editorRef.hide();
        }
      });
      return [];
    });
  } else setEditors((prevEditors) => prevEditors.filter((editor) => editor.type !== editorType));
};

const findEditorByType = (editorType, editors) => editors.find((editor) => editor.type === editorType);
const findEditorById = (recordIdOrObj, editorType, editors) => {
  return editors.find((editor) => editor.type === editorType && editor.recordIdObj.id === recordIdOrObj.id);
};
// The `toFront` function here is a custom implementation, The param `true` here implies that the action was performed by user. See `EditorBase` detailed behavior
const bringEditorToFront = (editor) => editor.editorRef.toFront?.(true);
const updateEditorById = (existingEditorId, record, setEditors) => {
  setEditors((prevEditors) =>
    prevEditors.map((editor) => {
      if (editor.id === existingEditorId) {
        // if a record with the same id has been opened that means the editor is already opened, Just bring it to Front
        if (editor.editorRef) bringEditorToFront(editor);
        return { ...editor, record };
      } else return editor;
    })
  );
};

/**
 * @typedef {{ id: string, record: any|Object, type: string, className?: string }} EditorType
 */

/**
 * @typedef {{useExisting?:boolean, by?: "recordId"|"recordType", className?: string}} OpenEditorOptions
 */

export const EditorSpecs = {
  /**
   * @param {string} editorType The type of editor to open
   * @param {{id: string|number}} recordIdObj An object that has `id` property, this will be required for, in-case where "by" option value is "recordId", this can be just a string id but for cases where we create new record and id is resolved later
   * @param {any|Object} record The data that will be passed to the editor
   * @param {OpenEditorOptions} options The options for editor, by default, useExisting is true which means one editor of the same type will be opened at a ime
   * @return {EditorType}
   */
  openEditor: (_editorType, _recordIdObj, _record, _options) => {},
  /**
   * @description provide editor object as argument
   * @param {EditorType} editor
   */
  closeEditor: (_editor) => {},
  /**
   * @description Providing no or null argument will close all editors of any type
   * @param {string|null} editorType
   */
  closeAllEditors: (_editorType = null) => {},
  /**
   * @return {EditorType[]}
   */
  getEditors: () => {},
  /**
   * @description Tells if any editor is opened
   * @param {string?} ofType - not implemented yet
   * */
  isEditorsOpened: (_ofType) => {}
};

const useEditorHandler = () => {
  const [editors, setEditors] = useState(/** @type {EditorType[]} */ []);

  const openEditor = useCallback(
    /**
     * @param {string} editorType
     * @param {{id: string|number}} recordIdObj
     * @param {any|Object} record
     * @param {OpenEditorOptions} options
     */
    (editorType, recordIdObj, record, options = { useExisting: true, by: "recordId" }) => {
      if (options?.useExisting) {
        if (options.by === "recordId" && !recordIdObj) throw new Error('Please provide recordId as second param, If you set "by" option value to "recordId"');
        const existingEditor = options.by === "recordId" ? findEditorById(recordIdObj, editorType, editors) : findEditorByType(editorType, editors);
        if (existingEditor) {
          updateEditorById(existingEditor.id, record, setEditors);
          return;
        }
      }
      return addEditor(editorType, record, recordIdObj, setEditors, options);
    },
    [editors]
  );

  const closeEditor = useCallback((editor) => {
    removeEditorById(editor, setEditors);
  }, []);

  const closeAllEditors = useCallback((editorType = null) => {
    removeEditorsByType(setEditors, editorType);
  }, []);
  const getEditors = useCallback(() => editors, [editors]);
  const isEditorsOpened = useCallback(
    (_ofType) => {
      // we can further extend this function to offer opened editor query by type or id
      return editors?.length > 0;
    },
    [editors]
  );

  const updateEditor = useCallback((editorId, editorData, closeEditorsFilterFn = () => true) => {
    setEditors((prevEditors) => {
      return prevEditors
        .map((editor) => {
          if (editor.id === editorId) {
            return { ...editor, ...editorData };
          }
          return editor;
        })
        .filter(closeEditorsFilterFn);
    });
  }, []);

  return {
    editors,
    openEditor,
    closeEditor,
    closeAllEditors,
    getEditors,
    isEditorsOpened,
    updateEditor
  };
};

export default useEditorHandler;
